import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ArrowUpRight,
  Download,
  ExternalLink,
  Loader2,
  Mail,
  Phone,
  Search,
  X,
} from "lucide-react";
import { LogoMark } from "../components/Logo";
import { api } from "../config";
import { usePageMeta } from "../hooks/usePageMeta";

const STATUSES = ["new", "contacted", "call_booked", "quoted", "won", "lost"];

const STATUS_LABELS = {
  new: "New",
  contacted: "Contacted",
  call_booked: "Call booked",
  quoted: "Quoted",
  won: "Won",
  lost: "Lost",
};

const STATUS_STYLES = {
  new: "border-accent/40 bg-accent/10 text-[#FF8340]",
  contacted: "border-sky-400/30 bg-sky-400/10 text-sky-300",
  call_booked: "border-teal-400/30 bg-teal-400/10 text-teal-300",
  quoted: "border-amber-400/30 bg-amber-400/10 text-amber-300",
  won: "border-ok/30 bg-ok/10 text-ok",
  lost: "border-bad/30 bg-bad/10 text-bad",
};

function formatPhone(digits) {
  let d = digits || "";
  if (d.length === 11 && d.startsWith("1")) d = d.slice(1);
  if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  return d;
}

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function formatDateTime(iso) {
  const d = new Date(iso);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function StatusPill({ status }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1 text-[12px] font-medium ${STATUS_STYLES[status] || STATUS_STYLES.new}`}
    >
      {status === "new" && <span className="h-1.5 w-1.5 rounded-full bg-accent" aria-hidden="true" />}
      {STATUS_LABELS[status] || status}
    </span>
  );
}

async function fetchLeads(params) {
  const qs = new URLSearchParams({
    q: params.q,
    status: params.status,
    sort: params.sort,
    page: String(params.page),
    per_page: "20",
  }).toString();
  const res = await fetch(api(`/admin/leads?${qs}`), { credentials: "same-origin" });
  if (res.status === 401) throw new Error("unauthorized");
  if (!res.ok) throw new Error("failed");
  return res.json();
}

function LoginCard({ onSuccess }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch(api("/admin/login"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ password }),
      });
      if (res.ok) {
        onSuccess();
        return;
      }
      const body = await res.json().catch(() => ({}));
      setError(body.detail || "Incorrect password");
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-5">
      <form
        onSubmit={submit}
        className="card-cr w-full max-w-sm p-8"
        data-testid="admin-login-form"
      >
        <LogoMark size={36} />
        <h1 className="mt-6 font-serif text-3xl text-cream">Admin</h1>
        <p className="mt-2 text-sm text-dim">Enter your password to view leads.</p>
        <label htmlFor="admin-password" className="cr-label mt-7">
          Password
        </label>
        <input
          id="admin-password"
          type="password"
          className="cr-input"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          data-testid="admin-password-input"
        />
        {error && (
          <p className="field-error" role="alert" data-testid="admin-login-error">
            {error}
          </p>
        )}
        <button
          type="submit"
          className="btn-primary mt-6 w-full"
          disabled={loading}
          data-testid="admin-signin-button"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
        </button>
      </form>
    </div>
  );
}

function LeadDrawer({ lead, onClose, onSaved, onDeleted }) {
  const [status, setStatus] = useState(lead.status);
  const [notes, setNotes] = useState(lead.notes || "");
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    setStatus(lead.status);
    setNotes(lead.notes || "");
    setConfirmDelete(false);
  }, [lead]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(api(`/admin/leads/${lead.id}`), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ status, notes }),
      });
      if (!res.ok) throw new Error();
      const updated = await res.json();
      toast.success("Lead updated");
      onSaved(updated);
    } catch {
      toast.error("Could not save changes. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setSaving(true);
    try {
      const res = await fetch(api(`/admin/leads/${lead.id}`), {
        method: "DELETE",
        credentials: "same-origin",
      });
      if (!res.ok) throw new Error();
      toast.success("Lead deleted");
      onDeleted(lead.id);
    } catch {
      toast.error("Could not delete this lead.");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50" data-testid="lead-drawer">
      <button
        type="button"
        aria-label="Close details"
        onClick={onClose}
        className="absolute inset-0 h-full w-full cursor-pointer bg-black/60"
      />
      <div className="absolute inset-y-0 right-0 flex w-full flex-col bg-elev shadow-2xl sm:max-w-[460px] sm:border-l sm:border-line">
        <div className="flex items-center justify-between border-b border-line px-6 py-4">
          <h2 className="font-serif text-2xl text-cream">Lead details</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-[10px] border border-line text-dim transition-colors hover:text-cream"
            data-testid="drawer-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-6 overflow-y-auto px-6 py-6">
          <div>
            <p className="eyebrow">Name</p>
            <p className="mt-1 text-[16px] text-cream" data-testid="drawer-name">{lead.name}</p>
          </div>
          <div>
            <p className="eyebrow">Company</p>
            <p className="mt-1 text-[16px] text-cream">{lead.company}</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <p className="eyebrow">Phone</p>
              <a
                href={`tel:${lead.phone}`}
                className="mt-1 inline-flex items-center gap-2 text-[16px] text-cream hover:text-accent"
                data-testid="drawer-phone-link"
              >
                <Phone className="h-4 w-4 text-dim" />
                {lead.phone_display}
              </a>
            </div>
            <div>
              <p className="eyebrow">Email</p>
              <a
                href={`mailto:${lead.email}`}
                className="mt-1 inline-flex items-center gap-2 break-all text-[16px] text-cream hover:text-accent"
                data-testid="drawer-email-link"
              >
                <Mail className="h-4 w-4 shrink-0 text-dim" />
                {lead.email}
              </a>
            </div>
          </div>
          <div>
            <p className="eyebrow">Current website</p>
            {lead.website ? (
              <a
                href={lead.website}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-1 inline-flex items-center gap-2 break-all text-[16px] text-cream hover:text-accent"
              >
                <ExternalLink className="h-4 w-4 shrink-0 text-dim" />
                {lead.website}
              </a>
            ) : (
              <p className="mt-1 text-[16px] text-dim">None provided</p>
            )}
          </div>
          <div>
            <p className="eyebrow">Main services</p>
            {lead.services && lead.services.length > 0 ? (
              <ul className="mt-2 flex flex-wrap gap-2">
                {lead.services.map((s) => (
                  <li
                    key={s}
                    className="rounded-full border border-line px-3 py-1 text-[13px] text-cream/85"
                  >
                    {s}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-1 text-[16px] text-dim">None selected</p>
            )}
          </div>
          <div>
            <p className="eyebrow">Message</p>
            <p className="mt-1 whitespace-pre-wrap text-[16px] leading-relaxed text-cream/90">
              {lead.message || "—"}
            </p>
          </div>
          <div>
            <p className="eyebrow">Submitted</p>
            <p className="mt-1 text-[16px] text-cream/90">
              {formatDateTime(lead.created_at)} &middot; from {lead.source_page || "/"}
            </p>
          </div>

          <div className="border-t border-line pt-6">
            <label htmlFor="drawer-status" className="cr-label">
              Status
            </label>
            <select
              id="drawer-status"
              className="cr-select"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              data-testid="drawer-status-select"
            >
              {STATUSES.map((s) => (
                <option key={s} value={s} className="bg-subtle">
                  {STATUS_LABELS[s]}
                </option>
              ))}
            </select>
            <label htmlFor="drawer-notes" className="cr-label mt-5">
              Internal notes
            </label>
            <textarea
              id="drawer-notes"
              rows={5}
              className="cr-textarea"
              placeholder="Private notes about this lead…"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="drawer-notes-input"
            />
          </div>
        </div>

        <div className="border-t border-line px-6 py-4">
          {confirmDelete ? (
            <div className="flex flex-col gap-3 rounded-[10px] border border-bad/40 bg-bad/10 p-4" data-testid="delete-confirm">
              <p className="text-sm text-cream">Delete this lead permanently?</p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={remove}
                  disabled={saving}
                  className="inline-flex h-10 flex-1 cursor-pointer items-center justify-center rounded-[10px] bg-bad text-sm font-semibold text-[#0B0D0F] transition-colors hover:opacity-90"
                  data-testid="delete-confirm-button"
                >
                  Delete
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmDelete(false)}
                  className="btn-secondary h-10 flex-1 px-4 text-sm"
                  data-testid="delete-cancel-button"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="flex gap-3">
              <button
                type="button"
                onClick={save}
                disabled={saving}
                className="btn-primary h-11 flex-1"
                data-testid="drawer-save-button"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
              </button>
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                className="btn-secondary h-11 border-bad/40 text-bad hover:bg-bad/10"
                data-testid="drawer-delete-button"
              >
                Delete
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Dashboard({ onSignOut }) {
  const [searchInput, setSearchInput] = useState("");
  const [query, setQuery] = useState({ q: "", status: "all", sort: "newest", page: 1 });
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const debounceRef = useRef(null);

  const load = useCallback(async (q) => {
    try {
      const result = await fetchLeads(q);
      setData(result);
      setError("");
    } catch (e) {
      if (e.message === "unauthorized") onSignOut(true);
      else setError("Could not load leads. Please refresh.");
    }
  }, [onSignOut]);

  useEffect(() => {
    load(query);
  }, [query, load]);

  const onSearchChange = (value) => {
    setSearchInput(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setQuery((q) => ({ ...q, q: value, page: 1 }));
    }, 350);
  };

  const stats = [
    { label: "Total leads", value: data?.summary?.total },
    { label: "New", value: data?.summary?.new },
    { label: "Contacted", value: data?.summary?.contacted },
    { label: "Call booked", value: data?.summary?.call_booked },
    { label: "Won", value: data?.summary?.won },
  ];

  const leads = data?.leads || [];

  return (
    <div className="min-h-screen" data-testid="admin-dashboard">
      <header className="sticky top-0 z-30 border-b border-line bg-elev/95 backdrop-blur-md">
        <div className="container-cr flex h-16 items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <LogoMark size={26} />
            <span className="font-semibold text-cream">Leads</span>
          </div>
          <div className="flex items-center gap-3">
            <a
              href={api("/admin/leads/export.csv")}
              download
              className="btn-secondary h-10 px-4 text-sm"
              data-testid="export-csv-button"
            >
              <Download className="h-4 w-4" />
              <span className="hidden sm:inline">Export CSV</span>
            </a>
            <button
              type="button"
              onClick={() => onSignOut(false)}
              className="btn-secondary h-10 px-4 text-sm"
              data-testid="signout-button"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="container-cr py-8">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" data-testid="stats-row">
          {stats.map((s) => (
            <div key={s.label} className="card-cr p-4">
              <p className="text-[12px] font-medium uppercase tracking-[0.1em] text-dim">
                {s.label}
              </p>
              <p className="mt-1.5 text-2xl font-semibold text-cream" data-testid={`stat-${s.label.replace(/\s+/g, "-").toLowerCase()}`}>
                {s.value ?? "—"}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-dim" />
            <input
              type="search"
              className="cr-input pl-10"
              placeholder="Search name, company, email, or phone…"
              aria-label="Search leads"
              value={searchInput}
              onChange={(e) => onSearchChange(e.target.value)}
              data-testid="leads-search-input"
            />
          </div>
          <select
            className="cr-select sm:w-44"
            aria-label="Filter by status"
            value={query.status}
            onChange={(e) => setQuery((q) => ({ ...q, status: e.target.value, page: 1 }))}
            data-testid="status-filter"
          >
            <option value="all" className="bg-subtle">All statuses</option>
            {STATUSES.map((s) => (
              <option key={s} value={s} className="bg-subtle">
                {STATUS_LABELS[s]}
              </option>
            ))}
          </select>
          <select
            className="cr-select sm:w-36"
            aria-label="Sort leads"
            value={query.sort}
            onChange={(e) => setQuery((q) => ({ ...q, sort: e.target.value, page: 1 }))}
            data-testid="sort-select"
          >
            <option value="newest" className="bg-subtle">Newest first</option>
            <option value="oldest" className="bg-subtle">Oldest first</option>
          </select>
        </div>

        {error && (
          <p className="field-error mt-4" role="alert">
            {error}
          </p>
        )}

        {data && leads.length === 0 ? (
          <div className="card-cr mt-6 flex flex-col items-center gap-3 p-14 text-center" data-testid="leads-empty-state">
            <LogoMark size={32} className="opacity-60" />
            <p className="text-[16px] text-dim">
              No leads yet. Share your site link to start collecting requests.
            </p>
          </div>
        ) : (
          data && (
            <>
              {/* Desktop table */}
              <div className="card-cr mt-6 hidden overflow-hidden lg:block" data-testid="leads-table-desktop">
                <table className="w-full table-fixed border-collapse text-left">
                  <thead>
                    <tr className="border-b border-line text-[12px] uppercase tracking-[0.1em] text-dim">
                      <th className="w-[130px] px-5 py-3.5 font-medium">Date</th>
                      <th className="px-5 py-3.5 font-medium">Name / Company</th>
                      <th className="w-[170px] px-5 py-3.5 font-medium">Phone</th>
                      <th className="w-[160px] px-5 py-3.5 font-medium">Status</th>
                      <th className="w-[90px] px-5 py-3.5 font-medium" />
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((lead) => (
                      <tr
                        key={lead.id}
                        className="border-b border-line/60 transition-colors last:border-0 hover:bg-subtle/60"
                      >
                        <td className="truncate px-5 py-4 text-sm text-dim">
                          {formatDate(lead.created_at)}
                        </td>
                        <td className="px-5 py-4">
                          <p className="truncate text-[15px] font-medium text-cream">{lead.name}</p>
                          <p className="truncate text-sm text-dim">{lead.company}</p>
                        </td>
                        <td className="truncate px-5 py-4 text-sm text-cream/85">
                          {formatPhone(lead.phone)}
                        </td>
                        <td className="px-5 py-4">
                          <StatusPill status={lead.status} />
                        </td>
                        <td className="px-5 py-4 text-right">
                          <button
                            type="button"
                            onClick={() => setSelected(lead)}
                            className="inline-flex h-9 cursor-pointer items-center rounded-[8px] border border-line px-4 text-sm font-medium text-cream transition-colors hover:border-accent/50 hover:text-accent"
                            data-testid={`view-lead-${lead.id}`}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile / tablet cards */}
              <div className="mt-6 flex flex-col gap-3 lg:hidden" data-testid="leads-cards-mobile">
                {leads.map((lead) => (
                  <div key={lead.id} className="card-cr p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-[16px] font-medium text-cream">{lead.name}</p>
                        <p className="truncate text-sm text-dim">{lead.company}</p>
                      </div>
                      <StatusPill status={lead.status} />
                    </div>
                    <div className="mt-4 flex items-center justify-between gap-3">
                      <div className="min-w-0 text-sm text-dim">
                        <p className="text-dim">{formatDate(lead.created_at)}</p>
                        <a
                          href={`tel:${lead.phone}`}
                          className="mt-0.5 inline-flex items-center gap-1.5 text-cream hover:text-accent"
                        >
                          <Phone className="h-3.5 w-3.5" />
                          {formatPhone(lead.phone)}
                        </a>
                      </div>
                      <button
                        type="button"
                        onClick={() => setSelected(lead)}
                        className="inline-flex h-10 cursor-pointer items-center rounded-[8px] border border-line px-4 text-sm font-medium text-cream transition-colors hover:border-accent/50 hover:text-accent"
                        data-testid={`view-lead-mobile-${lead.id}`}
                      >
                        View
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 flex items-center justify-between text-sm text-dim">
                <span data-testid="pagination-label">
                  Page {data.page} of {data.pages} &middot; {data.total} lead
                  {data.total === 1 ? "" : "s"}
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="btn-secondary h-10 px-4 text-sm disabled:cursor-not-allowed disabled:opacity-40"
                    disabled={data.page <= 1}
                    onClick={() => setQuery((q) => ({ ...q, page: q.page - 1 }))}
                    data-testid="pagination-prev"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    className="btn-secondary h-10 px-4 text-sm disabled:cursor-not-allowed disabled:opacity-40"
                    disabled={data.page >= data.pages}
                    onClick={() => setQuery((q) => ({ ...q, page: q.page + 1 }))}
                    data-testid="pagination-next"
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )
        )}
      </main>

      {selected && (
        <LeadDrawer
          lead={selected}
          onClose={() => setSelected(null)}
          onSaved={() => {
            load(query);
            setSelected(null);
          }}
          onDeleted={() => {
            setSelected(null);
            load(query);
          }}
        />
      )}
    </div>
  );
}

export default function Admin() {
  usePageMeta({ title: "Admin | Clearriver Studios", noindex: true });
  const [authed, setAuthed] = useState(null);

  const check = useCallback(async () => {
    try {
      await fetchLeads({ q: "", status: "all", sort: "newest", page: 1 });
      setAuthed(true);
    } catch {
      setAuthed(false);
    }
  }, []);

  useEffect(() => {
    check();
  }, [check]);

  const signOut = useCallback(async (expired) => {
    try {
      await fetch(api("/admin/logout"), { method: "POST", credentials: "same-origin" });
    } catch {
      /* ignore */
    }
    setAuthed(false);
    if (expired) toast.info("Your session expired. Please sign in again.");
  }, []);

  if (authed === null) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-dim" />
      </div>
    );
  }
  if (!authed) return <LoginCard onSuccess={() => setAuthed(true)} />;
  return <Dashboard onSignOut={signOut} />;
}
